/**
 * Geometría compartida del shell de los modos — la fila de contenido y el panel de
 * configuración — para los DOS forks del sheet (`PresetDetailSheet` y
 * `PresetDetailSheetModelo`).
 *
 * Existe porque cada fork tenía su propia copia de estas constantes y un arreglo de
 * layout había que hacerlo dos veces: el 04/09 Automática y Catálogo tomaron el fix de
 * la columna de resultados y Modelo se quedó con la columna fija hasta que alguien lo
 * notó (nota de ADR-2026-09-04-la-columna-de-resultados-toma-el-ancho-que-sobra). Los
 * sheets siguen forkeados a propósito —son layouts distintos—, pero la geometría que
 * comparten vive en UN lugar (ADR-2026-09-06-constantes-de-layout-compartidas-entre-los-dos-sheets).
 *
 * Son strings de clases y de CSS, no hay React acá. Tailwind las compila igual que si
 * estuvieran en el `.tsx`: el `content` escanea `src/**\/*.ts`.
 */

/**
 * Ancho del panel de CONFIGURACIÓN — el mismo en Modelo, Catálogo y Automático, y el
 * mismo en todos los pasos.
 *
 * Antes cada paso lo medía a su manera: 35% de la fila al elegir productos, 320px en los
 * pasos 2 y 3 del Catálogo, hasta 1040px cuando la configuración se toma la pantalla. El
 * panel es el MISMO —cambia lo que muestra adentro, no lo que es—, así que cambiar de
 * paso lo hacía saltar de ancho y cada salto se leía como una pantalla nueva
 * (ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660).
 *
 * Un solo valor, pero ELÁSTICO: 660px es la medida en un desktop de 1920px —de ahí el
 * 34.375vw, que es exactamente eso— y de ahí para abajo el panel achica con la ventana en
 * vez de empujar al panel lateral fuera de la pantalla. El tope de 660px lo deja quieto
 * en monitores más grandes (crecer no muestra nada más) y el piso de 420px es lo que
 * necesitan la fila de Calidad + Formato y la de cantidad + Generar para no romperse.
 * Ver ADR-2026-09-02-el-panel-de-configuracion-achica-con-la-ventana.
 *
 * **2026-09-06:** bajó de `clamp(420px, 34.375vw, 660px)` a `clamp(400px, 32vw, 600px)`
 * —600px en 1920 y 547 en 1708, un 7% menos— y pasó a valer TAMBIÉN en Fotos de
 * producto, que usaba el ancho angosto del Catálogo (400px) y hacía que dos modos
 * hermanos abrieran con paneles de distinto tamaño
 * (ADR-2026-09-06-un-solo-ancho-de-panel-para-los-modos).
 */
export const ANCHO_PANEL_CONFIG = 'clamp(400px, 32vw, 600px)';

/**
 * Ancho del panel de configuración en los pasos 2 y 3 del Catálogo, y en el paso 2 de
 * Fotos de producto.
 *
 * Ahí el panel deja de ser lo que se opera: en el 2 lo que se elige son las VISTAS y en
 * el 3 se revisa el catálogo, las dos cosas en la columna de al lado. El panel queda de
 * recordatorio —qué productos, qué fondo, con qué calidad— más el Generar, y con 660px
 * sostenía cuatro filas en media pantalla mientras la grilla que sí se está usando
 * quedaba con tarjetas de 93px y los nombres desbordados sobre la foto.
 *
 * 400px. Vienen de los 320 de ADR-2026-08-27-el-panel-del-paso-2-baja-a-320 —el mínimo que
 * necesitan la card de Productos con su miniatura de 52px y el segmentado de Calidad con
 * sus tres opciones—, que quedaban justos: el nombre del formato ("Banner Web (16:9)") y
 * el del fondo ocupaban la fila entera sin aire
 * (ADR-2026-09-04-el-panel-del-paso-2-crece-un-15).
 *
 * Lo comparten Catálogo y Fotos de producto: los dos pasos 2 son la misma pantalla —panel
 * de recordatorio + grilla al lado— desde
 * ADR-2026-09-04-fotos-de-producto-toma-el-diseno-del-catalogo, así que el ancho es uno
 * solo y no dos números que se van a separar.
 *
 * Supersede, para estos dos pasos, a ADR-2026-09-02-el-panel-de-configuracion-mide-siempre-660.
 * Lo que ese ADR quería evitar —que avanzar de paso se lea como una pantalla nueva— se
 * resuelve por otro lado: la columna conserva las MISMAS cards, en el mismo orden y con
 * la misma forma, y ya venía sin título (ADR-2026-08-27-el-panel-de-configuracion-no-lleva-titulo).
 * Ver ADR-2026-09-04-el-panel-se-angosta-en-los-pasos-2-y-3-del-catalogo.
 *
 * Sólo lo usa el sheet genérico (Modelo no tiene esos pasos); vive acá igual para que
 * los anchos del panel estén todos juntos.
 */
export const ANCHO_PANEL_CONFIG_CATALOGO = '400px';

/**
 * La columna de contenido del shell, para los modos que se presentan como sección —
 * ADR-2026-08-03-modos-usan-la-columna-de-inicio.
 *
 * `/inicio`, `/plantillas`, `/creaciones` y `/mi-marca` viven adentro del wrapper
 * `.shell-seccion`, que es `mx-auto max-w-[90%]` + 32px de sangría. Los modos son
 * un panel `fixed`: el wrapper no los alcanza, y con sólo `px-8` su contenido
 * arrancaba ~67px más a la izquierda que el de todas las otras pantallas del shell.
 *
 * Se aplica bloque por bloque —al encabezado, al título y a la fila de contenido— y
 * no en un `<div>` que envuelva a los tres: la fila depende de ser hija directa del
 * panel para su `flex-1 min-h-0` — un contenedor de por medio corta esa cadena y el
 * panel deja de medir lo que la ventana. El `max-w` de cada bloque se resuelve contra
 * el panel, que sí es su bloque contenedor; un `padding: 5%` en el panel NO serviría,
 * porque los porcentajes de padding de un elemento posicionado se resuelven contra SU
 * bloque contenedor (la ventana), no contra su propio ancho.
 *
 * La fila la lleva en los DOS estados de la columna derecha —con la muestra del modo y
 * con las tandas de resultados—. Hubo una variante "hasta el borde" (`lg:pl-[5%]
 * lg:!pr-0`) sólo para las tandas: se comía los 32px del `px-8` de la fila, el aside
 * saltaba a la izquierda y la columna crecía 5%·P + 64px, que se leía como que el
 * panel "se achicaba" al generar. Se borró
 * (ADR-2026-09-06-la-columna-de-resultados-ocupa-la-misma-caja).
 */
export const COLUMNA_SECCION = 'lg:mx-auto lg:w-full lg:max-w-[90%]';

/**
 * La FILA de contenido (panel + columna) de los cuatro modos con el rediseño —
 * ADR-2026-09-06-la-fila-del-modo-arranca-a-52px-y-llega-al-borde.
 *
 * Reemplaza a `COLUMNA_SECCION` en esos modos por dos motivos que van juntos:
 *
 * 1. **A la izquierda, 52px y no ~104.** El 90% centrado dejaba `0.05·P + 32px` de
 *    sangría (104px en 1708), muy por encima del aire que hay ARRIBA de la misma card:
 *    8px del `lg:inset-y-2` del panel + los 52 del `lg:pt-[52px]`. Como entre la
 *    sidebar y el panel ya hay 8px estructurales, igualar los dos aires deja el padding
 *    en exactamente 52 — el mismo número que el de arriba, que es lo que hace que se
 *    lea parejo por los cuatro lados.
 * 2. **A la derecha, los mismos 52px.** La columna llega casi hasta el borde de la
 *    ventana —el panel de estos modos es `lg:!right-0`— con el mismo aire que arriba y
 *    que a la izquierda, así la pantalla se lee pareja por los cuatro lados. (Estuvo en
 *    cero un rato: pegaba la barra de scroll al borde, pero dejaba la caja descentrada
 *    respecto del resto del marco.)
 *
 * La MISMA clase en los dos estados de la columna —muestra del modo y resultados—: que
 * cambiara entre uno y otro es lo que movía el panel al generar
 * (ADR-2026-09-06-la-columna-de-resultados-ocupa-la-misma-caja).
 *
 * Los modos-sección que NO entraron al rediseño (Anuncio, Infografía, Catálogo de
 * muebles) siguen con `COLUMNA_SECCION`: su encabezado se dibuja cruzando las dos
 * columnas con esa misma clase, y moverle la fila sola los desalinearía.
 */
export const FILA_MODO_REDISENO = 'lg:!pl-[52px] lg:!pr-[52px]';

/**
 * La columna de RESULTADOS —las tandas— se sale de los paddings de la fila y del panel
 * para llegar al borde arriba, abajo y a la derecha —
 * ADR-2026-09-06-la-columna-de-resultados-llega-al-marco.
 *
 * Son márgenes NEGATIVOS sobre la columna y no menos padding en la fila, y esa es toda
 * la diferencia con el intento anterior: `FILA_MODO_REDISENO` queda intacto, así que el
 * panel de configuración de la izquierda arranca en el mismo lugar con la muestra del
 * modo y con las tandas. Cambiarle el padding a la fila era lo que movía el panel al
 * generar y lo que hizo revertir a ADR-2026-09-04-la-columna-de-resultados-llega-al-borde
 * (ver ADR-2026-09-06-la-columna-de-resultados-ocupa-la-misma-caja).
 *
 * Cada margen cancela exactamente un padding: `-mb-6` el `pb-6` de la fila, `-mr-8` /
 * `lg:-mr-[52px]` su `px-8` y el `lg:!pr-[52px]` de `FILA_MODO_REDISENO`, y
 * `lg:-mt-[52px]` el `lg:pt-[52px]` del panel del modo (que en estos pasos existe
 * siempre: `contenidoScrollea` es false a dos columnas). El panel es `overflow-hidden`,
 * así que lo que se desborda hacia su padding se sigue viendo y se corta recién en el
 * marco flotante del shell —los 8px y las esquinas de 16px de `MARCO_PANTALLA`, que son
 * los mismos de la sidebar y no se tocan—.
 */
export const COLUMNA_RESULTADOS_AL_BORDE =
  '-mb-6 -mr-8 lg:-mr-[52px] lg:-mt-[52px]';

/**
 * El aire de arriba y de abajo de la columna de resultados, ADENTRO de su scroller.
 *
 * Los mismos números que `COLUMNA_RESULTADOS_AL_BORDE` cancela, devueltos como padding
 * del contenedor de scroll: en reposo la primera fila de cards queda a la misma altura
 * que el techo del panel de configuración de al lado y la última conserva su aire al
 * final de la lista, pero los dos SCROLLEAN con las cards. Sin esto la grilla arrancaba
 * pegada al marco y las dos columnas se leían desalineadas; con el padding en la columna
 * (afuera del scroller) volvía a ser una franja muerta que nunca se va.
 *
 * A la derecha no se devuelve nada —ahí el borde es justamente lo que se quiere sacar—:
 * el aire contra la barra de scroll lo pone el `pr-2` de `PanelTandaCreaciones`, que va
 * del lado de adentro de la barra.
 */
export const COLUMNA_RESULTADOS_AIRE_INTERNO = 'pb-6 lg:pt-[52px]';
